import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { parseResponse, registerErrorMessage } from '@skynet/data-ws-core';
import { actionOnMessage } from '@skynet/data-ws-mm';
import type { JsonFailure } from '@skynet/data-ws-core';

import { cloneDeep } from 'es-toolkit';

import { ExchangeLiquidityCommands } from './types';
import type { JsonExchangeLiquiditySuccess, ExchangeLiquidityState } from './types';

const allowedCommands = Object.values(ExchangeLiquidityCommands);

registerErrorMessage(ExchangeLiquidityCommands.LoanExchanges, {
  title: 'Exchanges Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

export const initialState: ExchangeLiquidityState = {
  requestStatus: null,
  error: null,

  // LoanExchanges, data on Listen
  list: [],
  supportedPairs: [],
  supportedExchanges: [],
  listCache: {},

  hideNotSupportExchange: true,
};

const exchangeLiquiditySlice = createSlice({
  name: 'exchanges', // e.g. loans.exchanges / retainers overview liquidity
  initialState,
  reducers: {
    resetState: (state) => {
      const clonedInitialState = cloneDeep(initialState);
      state.hideNotSupportExchange = true;
      clonedInitialState.listCache = state.listCache;

      return clonedInitialState;
    },
    toggleEnableFilter: (state) => {
      state.hideNotSupportExchange = !state.hideNotSupportExchange;
    },
    loadCache: (state, action: PayloadAction<{ loanId: string }>) => {
      state.list = state.listCache[action.payload.loanId] || [];
    },
    /**
     * Marks the request as in-flight. Dispatched by the app when it starts
     * listening for the overview/liquidity sections (the app owns the
     * `listen` action, which this package cannot import).
     */
    setRequestPending: (state) => {
      state.requestStatus = 'pending';
    },
  },
  extraReducers: (builder) => {
    // on WS message
    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error } = parseResponse(action.payload, allowedCommands);

      if (skipProcessing || !json) return;

      const { command } = json;

      if (error) {
        state.error = error;
        return;
      }

      const { id } = json;

      if (command === ExchangeLiquidityCommands.LoanExchanges) {
        const { exchanges, supportedExchanges, pairs }: JsonExchangeLiquiditySuccess = json;

        state.list = exchanges;
        state.supportedExchanges = supportedExchanges;
        state.supportedPairs = pairs;

        state.listCache[id] = exchanges;

        state.requestStatus = 'success';
      }
    });
  },
});

export const exchangeLiquidityActions = { ...exchangeLiquiditySlice.actions };

export default exchangeLiquiditySlice.reducer;
